class MegaMenu extends HTMLElement {
  constructor() {
    super();
    this.overlay = this.querySelector('[data-mega-menu-overlay]');
    this.mobileToggle = this.querySelector('[data-mega-menu-mobile-toggle]');
    this.mobileDrawer = this.querySelector('[data-mega-menu-mobile-drawer]');
    this.desktopTriggers = this.querySelectorAll('[data-mega-menu-trigger]');
    this.desktopSplitGroups = this.querySelectorAll('.mega-menu__nav-split');
    this.desktopPanels = this.querySelectorAll('[data-mega-menu-panel]');
    this.closeBtns = this.querySelectorAll('[data-mega-menu-close]');
    this.mobileAccordionTriggers = this.querySelectorAll('[data-mobile-accordion-trigger]');

    this.activeDesktopPanel = null;
    this.activeTrigger = null;
    this.desktopCloseTimeout = null;

    this.boundHandleKeyDown = this.handleKeyDown.bind(this);
    this.boundHandlePanelFocusOut = this.handlePanelFocusOut.bind(this);
  }

  connectedCallback() {
    // Desktop triggers - open on hover and click
    this.desktopTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => this.handleDesktopTriggerClick(e));
      trigger.addEventListener('mouseenter', (e) => this.handleDesktopTriggerEnter(e));
      trigger.addEventListener('mouseleave', () => this.scheduleDesktopClose());
    });

    // Split groups - hovering the link part also opens the panel
    this.desktopSplitGroups.forEach((group) => {
      const link = group.querySelector('.mega-menu__nav-link--split');
      const trigger = group.querySelector('[data-mega-menu-trigger]');
      if (link && trigger) {
        link.addEventListener('mouseenter', () => {
          if (window.innerWidth < 1024) return;
          this.cancelDesktopClose();
          const panelId = trigger.getAttribute('data-mega-menu-trigger');
          const panel = this.querySelector(`[data-mega-menu-panel="${panelId}"]`);
          this.openDesktopPanel(panel, trigger, false);
        });
        link.addEventListener('mouseleave', () => this.scheduleDesktopClose());
        group.addEventListener('mouseleave', () => this.scheduleDesktopClose());
      }
    });

    // Desktop panels - cancel close on enter, schedule close on leave
    this.desktopPanels.forEach((panel) => {
      panel.addEventListener('mouseenter', () => this.cancelDesktopClose());
      panel.addEventListener('mouseleave', () => this.scheduleDesktopClose());
    });

    // Mobile toggle
    if (this.mobileToggle) {
      this.mobileToggle.addEventListener('click', () => this.toggleMobile());
    }

    // Close buttons
    this.closeBtns.forEach((btn) => {
      btn.addEventListener('click', () => this.closeMobile());
    });

    // Mobile accordion triggers
    this.mobileAccordionTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => this.toggleMobileAccordion(e));
    });

    // Overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => {
        this.closeDesktop();
        this.closeMobile();
      });
    }

    document.addEventListener('keydown', this.boundHandleKeyDown);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.boundHandleKeyDown);
  }

  // ---- Helpers ----

  cancelDesktopClose() {
    clearTimeout(this.desktopCloseTimeout);
  }

  getPanelFocusable(panel) {
    return Array.from(
      panel.querySelectorAll('a[href], button:not([disabled])')
    ).filter((el) => el.offsetParent !== null);
  }

  // ---- Desktop ----

  handleDesktopTriggerClick(e) {
    const panelId = e.currentTarget.getAttribute('data-mega-menu-trigger');
    const panel = this.querySelector(`[data-mega-menu-panel="${panelId}"]`);

    if (this.activeDesktopPanel === panel) {
      this.closeDesktop();
    } else {
      // Click always moves focus into the panel (keyboard-intent)
      this.openDesktopPanel(panel, e.currentTarget, true);
    }
  }

  handleDesktopTriggerEnter(e) {
    if (window.innerWidth < 1024) return;
    this.cancelDesktopClose();

    const panelId = e.currentTarget.getAttribute('data-mega-menu-trigger');
    const panel = this.querySelector(`[data-mega-menu-panel="${panelId}"]`);
    // Hover does not move focus
    this.openDesktopPanel(panel, e.currentTarget, false);
  }

  scheduleDesktopClose() {
    if (window.innerWidth < 1024) return;
    this.cancelDesktopClose();
    this.desktopCloseTimeout = setTimeout(() => {
      this.closeDesktop();
    }, 300);
  }

  openDesktopPanel(panel, trigger, moveFocus) {
    if (this.activeDesktopPanel && this.activeDesktopPanel !== panel) {
      this.activeDesktopPanel.classList.remove('is-active');
      this.activeDesktopPanel.setAttribute('aria-hidden', 'true');
      this.activeDesktopPanel.removeEventListener('focusout', this.boundHandlePanelFocusOut);
    }

    this.desktopTriggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));

    if (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
      this.activeTrigger = trigger;
    }

    panel.classList.add('is-active');
    panel.setAttribute('aria-hidden', 'false');
    this.activeDesktopPanel = panel;
    this.classList.add('is-desktop-open');

    // Listen for focus leaving the panel to close it
    panel.addEventListener('focusout', this.boundHandlePanelFocusOut);

    if (moveFocus) {
      const focusable = this.getPanelFocusable(panel);
      if (focusable.length > 0) focusable[0].focus();
    }
  }

  closeDesktop() {
    this.cancelDesktopClose();

    if (this.activeDesktopPanel) {
      this.activeDesktopPanel.classList.remove('is-active');
      this.activeDesktopPanel.setAttribute('aria-hidden', 'true');
      this.activeDesktopPanel.removeEventListener('focusout', this.boundHandlePanelFocusOut);
      this.activeDesktopPanel = null;
    }

    this.activeTrigger = null;
    this.desktopTriggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
    this.classList.remove('is-desktop-open');
  }

  handlePanelFocusOut(e) {
    // relatedTarget is where focus is moving to.
    // Ignore if focus is staying within the panel.
    // Also ignore Tab-key moves — those are handled explicitly in handleKeyDown
    // to give predictable close + return-focus behaviour.
    const panel = this.activeDesktopPanel;
    if (!panel) return;
    if (e.relatedTarget && panel.contains(e.relatedTarget)) return;
    // If focus moved to the trigger that owns this panel, also ignore
    // (happens on Shift+Tab from first item — handled in handleKeyDown).
    if (e.relatedTarget && e.relatedTarget === this.activeTrigger) return;
    this.closeDesktop();
  }

  // ---- Mobile ----

  toggleMobile() {
    if (this.classList.contains('is-mobile-open')) {
      this.closeMobile();
    } else {
      this.openMobile();
    }
  }

  openMobile() {
    this.classList.add('is-mobile-open');
    if (this.mobileToggle) {
      this.mobileToggle.setAttribute('aria-expanded', 'true');
    }
    document.body.classList.add('overflow-hidden');

    // Move focus into the drawer so screen readers announce it
    const closeBtn = this.mobileDrawer && this.mobileDrawer.querySelector('[data-mega-menu-close]');
    if (closeBtn) closeBtn.focus();
  }

  closeMobile() {
    this.classList.remove('is-mobile-open');
    if (this.mobileToggle) {
      this.mobileToggle.setAttribute('aria-expanded', 'false');
    }
    document.body.classList.remove('overflow-hidden');

    // Reset all accordions, then re-expand the default if applicable
    this.mobileAccordionTriggers.forEach((t) => {
      t.setAttribute('aria-expanded', 'false');
      const content = this.getAccordionContent(t);
      if (content) content.classList.remove('is-open');
    });

    // Re-expand default accordion
    const defaultTrigger = this.querySelector('[data-mobile-accordion-default]');
    if (defaultTrigger) {
      defaultTrigger.setAttribute('aria-expanded', 'true');
      const content = this.getAccordionContent(defaultTrigger);
      if (content) content.classList.add('is-open');
    }
  }

  // ---- Mobile Accordion ----

  getAccordionContent(trigger) {
    // For split items the trigger is inside .mega-menu__mobile-split,
    // so the accordion content is the next sibling of the parent div.
    if (trigger.closest('.mega-menu__mobile-split')) {
      return trigger.closest('.mega-menu__mobile-split').nextElementSibling;
    }
    return trigger.nextElementSibling;
  }

  toggleMobileAccordion(e) {
    const trigger = e.currentTarget;
    const content = this.getAccordionContent(trigger);
    if (!content) return;

    const isOpen = content.classList.contains('is-open');

    // Close all other accordions
    this.mobileAccordionTriggers.forEach((t) => {
      const c = this.getAccordionContent(t);
      if (c && c !== content) {
        c.classList.remove('is-open');
        t.setAttribute('aria-expanded', 'false');
      }
    });

    // Toggle current
    content.classList.toggle('is-open', !isOpen);
    trigger.setAttribute('aria-expanded', String(!isOpen));
  }

  // ---- Keyboard ----

  handleKeyDown(e) {
    if (e.key === 'Escape') {
      // Close any open accordion first
      const openAccordion = this.querySelector('.mega-menu__mobile-accordion-content.is-open');
      if (openAccordion && this.classList.contains('is-mobile-open')) {
        openAccordion.classList.remove('is-open');
        const prev = openAccordion.previousElementSibling;
        // For split items, the previous sibling is .mega-menu__mobile-split —
        // find the actual trigger button inside it.
        const trigger = prev && prev.classList.contains('mega-menu__mobile-split')
          ? prev.querySelector('[data-mobile-accordion-trigger]')
          : prev;
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      } else if (this.classList.contains('is-mobile-open')) {
        this.closeMobile();
      } else if (this.classList.contains('is-desktop-open')) {
        this.closeDesktop();
        if (this.activeTrigger) this.activeTrigger.focus();
      }
    }

    // Arrow key navigation between nav items (APG menubar pattern)
    if (this.classList.contains('is-desktop-open') || document.activeElement.closest('.mega-menu__nav')) {
      const navItems = Array.from(this.querySelectorAll('.mega-menu__nav-item:not([hidden])'));
      const triggers = navItems.map((li) =>
        li.querySelector('[data-mega-menu-trigger], .mega-menu__nav-link')
      ).filter(Boolean);
      const currentIndex = triggers.indexOf(document.activeElement);

      if (e.key === 'ArrowRight' && currentIndex !== -1) {
        e.preventDefault();
        const next = triggers[(currentIndex + 1) % triggers.length];
        next.focus();
        // Open panel if it's a trigger
        if (next.hasAttribute('data-mega-menu-trigger')) {
          const panelId = next.getAttribute('data-mega-menu-trigger');
          const panel = this.querySelector(`[data-mega-menu-panel="${panelId}"]`);
          this.openDesktopPanel(panel, next, false);
        } else {
          this.closeDesktop();
        }
      }

      if (e.key === 'ArrowLeft' && currentIndex !== -1) {
        e.preventDefault();
        const prev = triggers[(currentIndex - 1 + triggers.length) % triggers.length];
        prev.focus();
        if (prev.hasAttribute('data-mega-menu-trigger')) {
          const panelId = prev.getAttribute('data-mega-menu-trigger');
          const panel = this.querySelector(`[data-mega-menu-panel="${panelId}"]`);
          this.openDesktopPanel(panel, prev, false);
        } else {
          this.closeDesktop();
        }
      }

      if (e.key === 'ArrowDown' && document.activeElement.hasAttribute('data-mega-menu-trigger')) {
        e.preventDefault();
        const panelId = document.activeElement.getAttribute('data-mega-menu-trigger');
        const panel = this.querySelector(`[data-mega-menu-panel="${panelId}"]`);
        this.openDesktopPanel(panel, document.activeElement, true);
      }
    }

    if (e.key === 'Tab') {
      // ---- Desktop panel Tab handling ----
      if (this.activeDesktopPanel) {
        const panel = this.activeDesktopPanel;
        const focusable = this.getPanelFocusable(panel);
        const trigger = this.activeTrigger;

        // Tab from trigger into panel
        if (
          !e.shiftKey &&
          trigger &&
          document.activeElement === trigger
        ) {
          e.preventDefault();
          if (focusable.length > 0) focusable[0].focus();
          return;
        }

        // Tab forward past last panel item → close, return focus to trigger
        if (!e.shiftKey && focusable.length > 0 && document.activeElement === focusable[focusable.length - 1]) {
          e.preventDefault();
          this.closeDesktop();
          if (trigger) trigger.focus();
          return;
        }

        // Shift+Tab from first panel item → close, return focus to trigger
        if (e.shiftKey && focusable.length > 0 && document.activeElement === focusable[0]) {
          e.preventDefault();
          this.closeDesktop();
          if (trigger) trigger.focus();
          return;
        }
      }

      // ---- Mobile drawer Tab trap ----
      if (this.classList.contains('is-mobile-open')) {
        const drawer = this.mobileDrawer;
        if (!drawer) return;

        const focusable = drawer.querySelectorAll(
          'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;

        const visible = Array.from(focusable).filter(
          (el) => el.offsetParent !== null
        );
        if (visible.length === 0) return;

        const first = visible[0];
        const last = visible[visible.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  }
}

customElements.define('mega-menu', MegaMenu);
